import React, { FC, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import analytics from '../../infra/analytics';
import { BOOKING_RESULT, PAGE_VIEW_EVENT, SITE_NAME } from '../../constants/analytics';
import { TELECARE_PHONE, TELECARE_PHONE_HREF } from '../../constants/contact';
import { StepPage } from '../../components/StepPanel';
import { CoverType } from '../../model/MedicareCard';
import { needsMedicareClaimingTerm } from '../../services/booking';

// Handed over by the review step, as the booking itself is cleared once it is made.
export interface ConfirmedBooking {
  clinician?: string | null;
  when?: string | null;
  timezone?: string;
  coverType?: CoverType;
}

const NEXT_STEPS = [
  {
    title: 'Pay using the link in your SMS',
    detail: 'Pay by card, Apple Pay or Google Pay. If we haven\'t received your payment 3 business days before your appointment, we\'ll send you a reminder.',
  },
  {
    title: 'Your video link arrives by SMS and email',
    detail: 'Sent 3 business days and again 30 minutes before your appointment.',
  },
  {
    title: 'We may contact you about supporting documents',
    detail: 'The Telecare team may be in touch about supporting documents, diagnostic reports or questionnaires needed for this appointment.',
  },
];

const MEDICARE_STEP = {
  title: 'We lodge your Medicare claim within 3 business days of you attending your appointment',
  detail: 'Medicare pays any rebate straight into your nominated bank account — the rebate may vary based on your eligibility.',
};

const Thanks: FC = () => {
  const booking = (useLocation().state ?? {}) as ConfirmedBooking;

  useEffect(() => {
    analytics.logPageViewEvent(PAGE_VIEW_EVENT, {
      site: SITE_NAME,
      booking_result: 'success',
      page_name: BOOKING_RESULT,
    });
  }, []);

  // Without the hand-over (e.g. after a refresh) the claim step shows, as most patients claim Medicare.
  const steps = !booking.coverType || needsMedicareClaimingTerm(booking.coverType)
    ? [...NEXT_STEPS, MEDICARE_STEP]
    : NEXT_STEPS;

  return (
    <StepPage>
      <div className="confirm-hero">
        <div className="mk" aria-hidden>✓</div>
        <h2>Thank you for booking with Telecare</h2>
        <p>
          {booking.clinician && booking.when
            ? `Your appointment with ${booking.clinician} is confirmed for ${booking.when} — ${booking.timezone}.`
            : 'Your appointment is confirmed.'}
        </p>
      </div>

      <div className="notice n-ok" style={{ marginTop: 0 }}>
        <p>
          <b>No card details were collected on this page.</b>
          {' You will receive an email and SMS confirmation shortly. That SMS includes your payment link — please complete the payment to secure your appointment.'}
        </p>
      </div>

      <ol className="nextsteps">
        {steps.map(({ title, detail }, i) => (
          <li key={title} className="ns">
            <i>{i + 1}</i>
            <div>
              <b>{title}</b>
              {detail}
            </div>
          </li>
        ))}
      </ol>

      <div className="notice n-info" style={{ marginTop: 24 }}>
        <p>
          {'If you are having problems please press the chat icon or call '}
          <a href={TELECARE_PHONE_HREF}><b>{TELECARE_PHONE}</b></a>
          .
        </p>
      </div>

      <div className="nav">
        <div className="sp" />
        <a className="btn btn-g" href="https://www.telecare.com.au/">Back to Telecare home</a>
      </div>
    </StepPage>
  );
};

export default Thanks;
